import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { AssetSubClassCode, Entity } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { About } from '~/modules/about/About';
import { OverviewEventFeed } from '~/modules/overview-event-feed/OverviewEventFeed';
import { OverviewFinancials } from '~/modules/overview-financials/OverviewFinancials';
import { RevampThemeKnowledgeGraph } from '~/modules/overview-knowledge-graph/components/theme-knowledge-graph/revamp-theme-knowledge-graph/RevampThemeKnowledgeGraph';
import { OverviewValuation } from '~/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { ChartState } from '~/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { ThemeDrawer } from '~/widgets/theme-widget/theme-drawer/ThemeDrawer';

import { BrandsAndProductsSection } from '../knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { RevampCompetitorsSection } from '../knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/revamp-competitors-section/RevampCompetitorsSection';
import { EarningsInsightSection } from '../knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { useEarningsInsightsByEntity } from '../knowledge-graph-widget/components/earnings-insight-section/useEarningsInsightsByEntity';
import { RevampGeographicExposureSection } from '../knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/revamp-geographic-exposure-section/RevampGeographicExposureSection';
import { UpgradeToUnlock } from './components/upgrade-to-unlock/UpgradeToUnlock';
import * as S from './Overview.styles';

export interface OverviewProps {
  entity: Entity;
}

export const RevampOverview = ({ entity }: OverviewProps) => {
  const chartNavigate = useChartNavigate();

  const { error: companyThemeError } = useCompanyTheme({
    entityTag: entity.tag,
  });

  const {
    earningsData,
    isLoading: isEarningsLoading,
    earningsInsight,
  } = useEarningsInsightsByEntity(entity.tag);

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneHour,
    horizon: RangeHorizon.OneMonth,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    chartNavigate([entityValue], {
      resample: chartState.resample,
      horizon: chartState.horizon,
      seriesType: chartState.chartPanes[0]?.seriesType,
    });
  };

  const displayConnections = companyThemeError?.status !== 404 && isStock;
  return (
    <>
      <ThemeDrawer entity={entity} />
      <S.OverviewRoot>
        <SimpleChart
          entity={entity}
          key={entity.tag}
          initialChartSettings={chartSettings}
          isLegacyVariant={false}
          onClickFullscreen={navigateToChart}
          maxHorizon={RangeHorizon.TenYears}
          eventsConfig={{
            TMI: true,
          }}
        />
        <S.SectionsWrapper>
          <S.Flex>
            <S.EventFeedWrapper>
              {isBasicOrLightUser ? (
                <S.UpgradeToUnlockWrapper>
                  <UpgradeToUnlock />
                </S.UpgradeToUnlockWrapper>
              ) : (
                <ErrorBoundaryWrapper>
                  <OverviewEventFeed entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              )}
            </S.EventFeedWrapper>
            <S.RightColumn>
              <ErrorBoundaryWrapper>
                <EarningsInsightSection
                  entity={entity}
                  earningsData={earningsData}
                  earningsInsight={earningsInsight}
                  isLoading={isEarningsLoading}
                  isPreview={earningsInsight?.type === 'preview'}
                />
              </ErrorBoundaryWrapper>
              <S.StyledAboutWrapper>
                <About
                  entity={entity}
                  variant="secondary"
                  showMoreDescription
                  limit={600}
                />
              </S.StyledAboutWrapper>
            </S.RightColumn>
          </S.Flex>
          {displayConnections && (
            <>
              <S.Flex>
                <S.SplitSection>
                  <ErrorBoundaryWrapper>
                    <RevampThemeKnowledgeGraph entityTag={entity.tag} />
                  </ErrorBoundaryWrapper>
                </S.SplitSection>
                <S.SplitSection>
                  <ErrorBoundaryWrapper>
                    <RevampCompetitorsSection entityTag={entity.tag} />
                  </ErrorBoundaryWrapper>
                </S.SplitSection>
              </S.Flex>
              <S.Flex>
                <S.SplitSection>
                  <ErrorBoundaryWrapper>
                    <RevampGeographicExposureSection entityTag={entity.tag} />
                  </ErrorBoundaryWrapper>
                </S.SplitSection>
                <S.SplitSection>
                  <ErrorBoundaryWrapper>
                    <BrandsAndProductsSection entityTag={entity.tag} />
                  </ErrorBoundaryWrapper>
                </S.SplitSection>
              </S.Flex>
            </>
          )}
        </S.SectionsWrapper>
        {isStock && (
          <>
            <OverviewFinancials entity={entity} />
            <OverviewValuation entity={entity} />
          </>
        )}
      </S.OverviewRoot>
    </>
  );
};
